import React from "react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { SimulatorComparisonQuota } from "./SimulatorComparisonQuota";

export function ViewAllSimulationsButton(props: {
  borderColor: string;
  subtleText: string;
  onClick: () => void;
}): React.ReactElement {
  const { borderColor, subtleText, onClick } = props;
  return (
    <Button
      size="sm"
      variant="outline"
      borderRadius="6px"
      borderColor={borderColor}
      color={subtleText}
      fontFamily="'IBM Plex Sans', sans-serif"
      textTransform="uppercase"
      fontSize="xs"
      onClick={onClick}
      flexShrink={0}
    >
      View all simulations
    </Button>
  );
}

export function SimulatorPageHero(props: {
  textColor: string;
  subtleText: string;
  borderColor: string;
  catalogVersion: string;
  policySchemaVersion: string;
  showPageTitle?: boolean;
  onOpenSavedBacktests?: () => void;
  /** When set (e.g. /simulator), show a back control above the title row. */
  onWizardExit?: () => void;
  wizardExitLabel?: string;
  /** In-session comparison runs loaded on this page (max 16). */
  comparisonRunsUsed?: number;
}): React.ReactElement {
  const {
    borderColor,
    subtleText,
    onOpenSavedBacktests,
    showPageTitle,
    onWizardExit,
    wizardExitLabel,
    comparisonRunsUsed,
  } = props;
  const shadcnInset = useShadcnInsetChrome();
  const showTitle = Boolean(showPageTitle && !shadcnInset);
  const showHeroCopy = Boolean(showPageTitle);
  return (
    <Box mb={showHeroCopy ? 4 : 2}>
      {onWizardExit ? (
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          size="sm"
          mb={3}
          color={subtleText}
          fontFamily="'IBM Plex Sans', sans-serif"
          onClick={onWizardExit}
        >
          {wizardExitLabel ?? "Back"}
        </Button>
      ) : null}
      <Flex align="flex-start" justify="space-between" gap={3} flexWrap="wrap" mb={2}>
        {showHeroCopy ? (
          <Box flex="1" minW={0}>
            {showTitle ? (
              <Text
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                color="#38BBD1"
                fontFamily="'Urbanist', sans-serif"
                fontWeight="normal"
                lineHeight="1.1"
                mb={2}
              >
                Simulator
              </Text>
            ) : null}
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color={subtleText}
              fontFamily="'IBM Plex Sans', sans-serif"
              maxW="720px"
              lineHeight="1.5"
            >
              Build execution rules, choose account and date scope, then compare baseline vs filtered trade performance.
              Save runs from the results section or open all saved simulations anytime.
            </Text>
          </Box>
        ) : (
          <Box flex="1" />
        )}
        <Flex align="center" gap={2} flexShrink={0} flexWrap="wrap" justify="flex-end">
          {comparisonRunsUsed != null ? (
            <SimulatorComparisonQuota used={comparisonRunsUsed} subtleText={subtleText} variant="pill" />
          ) : null}
          {onOpenSavedBacktests ? (
            <ViewAllSimulationsButton
              borderColor={borderColor}
              subtleText={subtleText}
              onClick={onOpenSavedBacktests}
            />
          ) : null}
        </Flex>
      </Flex>
    </Box>
  );
}
